import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { FilterState } from './types';

interface FiltersProps {
  filterState: FilterState;
  uniqueTypes: string[];
  uniqueSeries: string[];
  totalResources: number;
  filteredCount: number;
  onFilterChange: (filters: FilterState) => void;
  onClearFilters: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  filterState,
  uniqueTypes,
  uniqueSeries,
  totalResources,
  filteredCount,
  onFilterChange,
  onClearFilters
}) => {
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSeriesDropdown, setShowSeriesDropdown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Refs for click outside detection
  const typeDropdownRef = useRef(null);
  const seriesDropdownRef = useRef(null);

  const { selectedTypes, selectedSeries, searchTerm } = filterState;

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(event.target)) {
        setShowSeriesDropdown(false);
      }
    };

    if (showTypeDropdown || showSeriesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeDropdown, showSeriesDropdown]);

  const styles = {
    filterSection: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px',
      transition: 'all 0.3s ease'
    },
    collapsedHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 24px',
      cursor: 'pointer',
      borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
      transition: 'background-color 0.2s'
    },
    collapsedContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: 1
    },
    collapsedTitle: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1f2937'
    },
    expandedContent: {
      padding: '24px',
      display: isExpanded ? 'block' : 'none'
    },
    filterHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    clearButton: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 12px',
      fontSize: '1rem',
      color: '#2563eb',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    expandButton: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      fontSize: '1rem',
      color: '#2563eb',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px',
      marginBottom: '16px'
    },
    searchInput: {
      width: '100%',
      padding: '8px 8px 8px 40px',
      border: '1px solid #d1d5db',
      color: 'black',
      borderRadius: '8px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    searchContainer: {
      position: 'relative' as const
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    dropdown: {
      position: 'relative' as const
    },
    dropdownHeader: {
      width: '100%',
      padding: '8px 12px',
      fontSize: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      backgroundColor: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'border-color 0.2s'
    },
    dropdownContent: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      // marginTop: '4px',
      fontSize: '1rem',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      maxHeight: '200px',
      overflowY: 'auto' as const
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    checkbox: {
      marginRight: '12px',
      width: '16px',
      height: '16px'
    },
    resultsCount: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#6b7280'
    },
    activeFilters: {
      marginTop: '16px'
    },
    activeFiltersTitle: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '8px'
    },
    activeFiltersList: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px'
    },
    filterBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      fontSize: '1rem',
      fontWeight: '500',
      borderRadius: '9999px'
    },
    searchBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af'
    },
    typeBadge: {
      backgroundColor: '#f3e8ff',
      color: '#7c3aed'
    },
    seriesBadge: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    badgeButton: {
      marginLeft: '4px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0'
    },
    miniFilterBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 6px',
      fontSize: '0.75rem',
      fontWeight: '500',
      borderRadius: '4px'
    }
  };

  const updateFilters = (updates: Partial<FilterState>) => {
    onFilterChange({ ...filterState, ...updates });
  };

  const toggleType = (type: string) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    updateFilters({ selectedTypes: newTypes });
  };

  const toggleSeries = (series: string) => {
    const newSeries = selectedSeries.includes(series)
      ? selectedSeries.filter(s => s !== series)
      : [...selectedSeries, series];
    updateFilters({ selectedSeries: newSeries });
  };

  const hasActiveFilters = selectedTypes.length > 0 || selectedSeries.length > 0 || searchTerm;

  return (
    <div style={styles.filterSection}>
      {/* Collapsed Header */}
      <div 
        style={styles.collapsedHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <div style={styles.collapsedContent}>
          <div style={styles.collapsedTitle}>
            <Filter size={20} style={{ marginRight: '8px' }} />
            Filters
          </div>          
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {hasActiveFilters && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearFilters();
              }}
              style={styles.clearButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X size={16} />
              Clear All
            </button>
          )}
          <button
            style={styles.expandButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dbeafe';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div style={styles.expandedContent}>
        <div style={styles.filterGrid}>
          {/* Search */}
          <div style={styles.searchContainer}>
            <div style={styles.searchIcon}>
              <Search size={16} />
            </div>
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => updateFilters({ searchTerm: e.target.value })}
              style={styles.searchInput}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>
          
          {/* Type Filter */}
          <div style={styles.dropdown} ref={typeDropdownRef}>
            <div 
              style={styles.dropdownHeader}
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              <span>
                {selectedTypes.length === 0 ? 'All Types' : `${selectedTypes.length} selected`}
              </span>
              <Filter size={16} color="#9ca3af" />
            </div>
            {showTypeDropdown && (
              <div style={styles.dropdownContent}>
                {uniqueTypes.map((type) => (
                  <label 
                    key={type} 
                    style={styles.checkboxLabel}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleType(type)}
                      style={styles.checkbox}
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          {/* Series Filter */}
          <div style={styles.dropdown} ref={seriesDropdownRef}>
            <div 
              style={styles.dropdownHeader}
              onClick={() => setShowSeriesDropdown(!showSeriesDropdown)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              <span>
                {selectedSeries.length === 0 ? 'All Series' : `${selectedSeries.length} selected`}
              </span>
              <Filter size={16} color="#9ca3af" />
            </div>
            {showSeriesDropdown && (
              <div style={styles.dropdownContent}>
                {uniqueSeries.map((series) => (
                  <label 
                    key={series} 
                    style={styles.checkboxLabel}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSeries.includes(series)}
                      onChange={() => toggleSeries(series)}
                      style={styles.checkbox}
                    />
                    <span>{series}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          {/* Results Count */}
          <div style={styles.resultsCount}>
            {filteredCount} of {totalResources} resources
          </div>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div style={styles.activeFilters}>
            <p className="p-style" style={styles.activeFiltersTitle}>Active filters:</p>
            <div style={styles.activeFiltersList}>
              {searchTerm && (
                <span style={{...styles.filterBadge, ...styles.searchBadge}}>
                  Search: "{searchTerm}"
                  <button
                    onClick={() => updateFilters({ searchTerm: '' })}
                    style={styles.badgeButton}
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {selectedTypes.map(type => (
                <span key={type} style={{...styles.filterBadge, ...styles.typeBadge}}>
                  Type: {type}
                  <button
                    onClick={() => toggleType(type)}
                    style={styles.badgeButton}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {selectedSeries.map(series => (
                <span key={series} style={{...styles.filterBadge, ...styles.seriesBadge}}>
                  Series: {series}
                  <button
                    onClick={() => toggleSeries(series)}
                    style={styles.badgeButton}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Filters;